import { Badge } from '@amore/ui/components/badge'
import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  CakeSlice,
  GlassWater,
  PencilLine,
  Plus,
  Users,
  UtensilsCrossed,
} from 'lucide-react'
import { supplierCategoryLabels, supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import type { ReceptionCategory } from '@/mock/reception'
import { useCanEdit, useWedding } from '@/mock/store'
import { EmptyState } from '@/patterns/empty-state'
import { LinkedRecord } from '@/patterns/linked-record'
import { receptionGuests } from './model'

const categories = [
  { category: 'food', label: 'Jídlo', icon: UtensilsCrossed },
  { category: 'drinks', label: 'Pití', icon: GlassWater },
  { category: 'sweets', label: 'Dort a sladké', icon: CakeSlice },
] satisfies { category: ReceptionCategory; label: string; icon: typeof UtensilsCrossed }[]

export function ReceptionPlanCard({ onPreview }: { onPreview: (title: string) => void }) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const { offerings } = data.reception
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <UtensilsCrossed className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Co máme v plánu</CardTitle>
        </div>
      </CardHeader>
      {offerings.length ? (
        <div className="divide-y divide-border px-5 sm:px-6">
          {categories.map(({ category, label, icon: Icon }) => {
            const items = offerings.filter((item) => item.category === category)
            if (!items.length) return null
            const supplierIds = new Set(items.flatMap((item) => item.supplierIds))
            const suppliers = data.suppliers.filter((supplier) => supplierIds.has(supplier.id))
            return (
              <section key={category} aria-label={label} className="space-y-4 py-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <Icon className="size-4 text-action" aria-hidden="true" />
                    {label}
                  </h3>
                  <Button
                    appearance="ghost"
                    tone="neutral"
                    size="sm"
                    aria-label={`Zobrazit ${label.toLocaleLowerCase('cs')}`}
                    onClick={() => onPreview(`Záložku ${label} připravujeme`)}
                  >
                    Zobrazit <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Button>
                </div>
                <ul className="space-y-3">
                  {items.map((item) => (
                    <li key={item.id} className="space-y-1">
                      <p className="text-sm font-medium">{item.name}</p>
                      <Text tone="muted">{item.description}</Text>
                    </li>
                  ))}
                </ul>
                {suppliers.map((supplier) => (
                  <LinkedRecord
                    key={supplier.id}
                    module="suppliers"
                    title={supplier.name}
                    meta={supplierCategoryLabels[supplier.category] ?? supplier.category}
                    status={
                      <Badge tone={supplierStatusTone(supplier.status)}>
                        {supplierStatusLabels[supplier.status]}
                      </Badge>
                    }
                    to={`/suppliers/${supplier.id}`}
                    actionLabel="Detail"
                  />
                ))}
                {items.some((item) => item.selfProvided) ? (
                  <Text variant="small" tone="muted">
                    {suppliers.length ? 'Část nápojů zajišťujeme sami.' : 'Zajišťujeme sami.'}
                  </Text>
                ) : null}
              </section>
            )
          })}
        </div>
      ) : (
        <div className="p-5 sm:p-6">
          <EmptyState
            placement="inline"
            icon={<UtensilsCrossed />}
            title="Co bude na vaší svatbě?"
            description={
              canEdit
                ? 'Menu, oblíbené pití nebo něco sladkého. Začněte tím, co už máte rozmyšlené. Všechno je volitelné.'
                : 'Jídlo, nápoje ani sladké zatím nejsou doplněné.'
            }
            actions={
              canEdit ? (
                <>
                  <Button onClick={() => onPreview('Přidání jídla připravujeme')}>
                    <Plus className="size-3.5" aria-hidden="true" /> Jídlo
                  </Button>
                  <Button
                    appearance="outline"
                    tone="neutral"
                    onClick={() => onPreview('Přidání pití připravujeme')}
                  >
                    Pití
                  </Button>
                  <Button
                    appearance="outline"
                    tone="neutral"
                    onClick={() => onPreview('Přidání sladkého připravujeme')}
                  >
                    Dort a sladké
                  </Button>
                </>
              ) : null
            }
          />
        </div>
      )}
    </Card>
  )
}

export function ReceptionGuests() {
  const data = useWedding()
  const guests = receptionGuests(data)
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Users className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Hosté a diety</CardTitle>
        </div>
      </CardHeader>
      <div className="space-y-4 p-5">
        {guests.total ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-2xl font-semibold tabular-nums">{guests.adults}</p>
                <Text variant="small" tone="muted">
                  dospělých
                </Text>
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">{guests.children}</p>
                <Text variant="small" tone="muted">
                  dětí
                </Text>
              </div>
            </div>
            <Text variant="small" tone="muted">
              Potvrzená účast.{guests.pending ? ` Bez odpovědi: ${guests.pending} hostů.` : ''}
            </Text>
            {guests.diets.length ? (
              <div className="space-y-3 border-t border-border pt-4">
                <div className="flex flex-wrap gap-2">
                  {guests.diets.map((diet) => (
                    <Badge key={diet.label} tone="neutral">
                      {diet.count}× {diet.label}
                    </Badge>
                  ))}
                </div>
                <Text variant="small" tone="muted">
                  Diety zahrnují i hosty bez odpovědi. Jeden host může mít víc požadavků.
                </Text>
              </div>
            ) : (
              <Text tone="muted">Zatím bez dietních požadavků.</Text>
            )}
            <div className="flex justify-end">
              <Link
                to="/guests"
                className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
              >
                Zobrazit hosty <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </>
        ) : (
          <Text tone="muted">
            Počty a stravovací požadavky se sem propíšou, jakmile doplníte seznam hostů.
          </Text>
        )}
      </div>
    </Card>
  )
}

export function ReceptionVendorMeals({ onPreview }: { onPreview: (title: string) => void }) {
  const { reception } = useWedding()
  const canEdit = useCanEdit()
  return (
    <Card>
      <div className="flex items-center gap-2.5">
        <UtensilsCrossed className="size-4 text-action" aria-hidden="true" />
        <CardTitle as="h2">Jídlo pro dodavatele</CardTitle>
      </div>
      <Text tone="muted" className="mt-3">
        {reception.vendorMeals === null
          ? 'Fotograf, kapela, koordinátorka… Porce navíc k hostům zatím nejsou domluvené.'
          : `Domluvené porce nad rámec hostů: ${reception.vendorMeals}.`}
      </Text>
      {canEdit ? (
        <div className="mt-3 flex justify-end">
          <Button
            appearance="ghost"
            tone="neutral"
            size="sm"
            onClick={() => onPreview('Domluvu jídla pro dodavatele připravujeme')}
          >
            <PencilLine className="size-3.5" aria-hidden="true" />
            {reception.vendorMeals === null ? 'Doplnit domluvu' : 'Upravit'}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}
